// src/component/alert-components/AlertConfigurationTab.jsx
import React, { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { Box, IconButton, Tooltip, Chip, useTheme } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import deviceStore from "../../stores/DeviceStore";
import CustomDataGridPro from "../CustomDataGridPro";

const AlertConfigurationTab = observer(() => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const devices = deviceStore.devices || [];
  const isLoading = deviceStore.isLoading;

  const columns = useMemo(
    () => [
      {
        field: "pimios_id",
        headerName: "Device ID",
        flex: 1.2,
        minWidth: 200,
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "typeName",
        headerName: "Type",
        flex: 0.8,
        minWidth: 150,
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.8,
        minWidth: 150,
        renderCell: (params) => {
          const isConfigured = !!params.row.alert_config_id;
          return (
            <Chip
              icon={
                isConfigured ? <CheckCircleOutlineIcon /> : <WarningAmberIcon />
              }
              label={isConfigured ? "Configured" : "Not Configured"}
              color={isConfigured ? "success" : "warning"}
              variant="outlined"
              sx={{
                fontWeight: 500,
                fontSize: "0.85rem",
                px: 1.5,
                borderRadius: 1.5,
                "& .MuiChip-icon": { fontSize: "1.2rem" },
              }}
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 120, // Keep fixed so the icon button doesn’t stretch
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Tooltip title="View Config">
            <IconButton
              onClick={() =>
                navigate(`/alerts/config/${params.row.pimios_id}`, {
                  state: { deviceRecord: params.row },
                })
              }
              size="small"
              sx={{ color: theme.palette.primary.main }}
            >
              <VisibilityOutlinedIcon />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [navigate, theme.palette.primary.main]
  );

  const rows = useMemo(
    () =>
      devices.map((device) => ({
        id: device.pimios_id,
        pimios_id: device.pimios_id,
        name: device.name,
        typeName: device.type_name || "Unknown",
        alert_config_id: device.alert_config_id,
      })),
    [devices]
  );

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <CustomDataGridPro
        rows={rows}
        columns={columns}
        isLoading={isLoading}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalCount={rows.length}
      />
    </Box>
  );
});

export default AlertConfigurationTab;
